/**
 * Makes Chase descriptions readable without renaming the merchant.
 *
 * Ported from `TransactionDisplay.swift`. This is the copy that used to live in
 * `aws-backend/src/lambda/domain/transaction-display.ts`; that module now
 * re-exports from here so the two can no longer drift.
 */

import { ledgerYear, parseLedgerDate } from './ledger';

/**
 * What kind of money movement a row is.
 *
 * Spending is the overwhelming majority and needs no label. Everything else —
 * money arriving, or your own money moving out of the account — is rare enough
 * that it deserves to be called out by name when you are sorting.
 */
export type TransactionFlow =
  | 'spending'
  | 'deposit'
  | 'refund'
  | 'transferIn'
  | 'transferOut'
  | 'withdrawal'
  | 'sent';

const FLOW_LABELS: Record<TransactionFlow, string | null> = {
  spending: null,
  deposit: 'Deposit',
  refund: 'Refund',
  transferIn: 'Transfer in',
  transferOut: 'Transfer out',
  withdrawal: 'Withdrawal',
  sent: 'Sent',
};

/** The word shown beside the amount. Ordinary spending says nothing. */
export function flowLabel(flow: TransactionFlow): string | null {
  return FLOW_LABELS[flow];
}

export function isMoneyIn(flow: TransactionFlow): boolean {
  return flow === 'deposit' || flow === 'refund' || flow === 'transferIn';
}

/** Money leaving the account that was not a purchase. */
export function isAccountMovement(flow: TransactionFlow): boolean {
  return flow === 'transferOut' || flow === 'withdrawal' || flow === 'sent';
}

/** Whole-word match, so a merchant like "ATMOSPHERE" is not a cash withdrawal. */
function containsWord(haystack: string, word: string): boolean {
  return new RegExp(`\\b${word}\\b`).test(haystack);
}

/**
 * Classify a sighting by what the money did.
 *
 * Chase's `type` is close to useless on a checking account — a coffee and a
 * wire can both arrive as MISC_DEBIT — so the description prefix carries most
 * of the signal and the sign settles the direction.
 */
export function transactionFlow(
  type: string,
  description: string,
  amount: number
): TransactionFlow {
  const haystack = `${type} ${description}`.toUpperCase();
  const incoming = amount > 0;

  if (containsWord(haystack, 'XFER') || haystack.includes('TRANSFER')) {
    return incoming ? 'transferIn' : 'transferOut';
  }
  if (!incoming && (containsWord(haystack, 'ATM') || haystack.includes('WITHDRAW'))) {
    return 'withdrawal';
  }
  if (incoming) {
    return haystack.includes('REFUND') ? 'refund' : 'deposit';
  }
  if (
    haystack.includes('PYMT SENT') ||
    haystack.includes('SENT MONEY') ||
    haystack.includes('ZELLE') ||
    haystack.includes('QUICKPAY')
  ) {
    return 'sent';
  }
  return 'spending';
}

const PREFIXES = [
  'POS DEBIT',
  'PYMT SENT',
  'ACH DEBIT',
  'ACH CREDIT',
  'DEBIT CRD',
  'CREDIT CRD',
  'MISC_DEBIT',
];

const SMALL_WORDS = new Set(['of', 'the', 'and', 'at', 'in', 'for']);

export function merchantName(raw: string): string {
  const collapsed = raw.replace(/\s+/g, ' ').trim();
  if (!collapsed) return raw;

  let s = origCoNameParts(collapsed) ?? collapsed;
  for (const prefix of PREFIXES) {
    if (s.toUpperCase().startsWith(prefix)) {
      s = s.slice(prefix.length).trim();
    }
  }

  s = s.replace(/^(SQ|TST|SP|PAYPAL)\s*\*\s*/i, '');
  s = s.replace(/^(WWP|TST|SQ)\*/i, '');
  s = s.replace(/\s+PPD\s+ID:\s*\S+/i, '');
  s = s.replace(/\s+(IND|ORIG|WEB)\s+ID:\s*\S+/i, '');
  s = s.replace(/\s+\d{3}-\d{3}-?\d{0,4}/g, '');
  s = s.replace(/\s+\d{1,2}\/\d{1,2}(\/\d{2,4})?$/, '');
  // Trailing Chase junk codes, not store numbers like #5488
  s = s.replace(/\s+\d{3,}$/, '');
  s = s.trim();

  const titled = titleCase(s);
  return titled || collapsed;
}

function origCoNameParts(raw: string): string | undefined {
  const match = raw.match(/ORIG\s+CO\s+NAME:(\S+)(?:\s+CO\s+ENTRY\s+DESCR:(\S+))?/i);
  if (!match) return undefined;
  const parts = [match[1]];
  if (match[2]) parts.push(match[2]);
  return parts.join(' ');
}

function titleCase(raw: string): string {
  const words = raw.split(' ').filter(Boolean);
  return words
    .map((word, index) => {
      const letters = word.replace(/[^A-Za-z]/g, '');
      const lower = word.toLowerCase();
      if (SMALL_WORDS.has(lower) && index > 0) return lower;
      if (letters.length > 3 && word !== lower && word !== word.toUpperCase()) {
        return word;
      }
      if (letters.length <= 3 && !/\d/.test(word)) {
        return word.toUpperCase();
      }
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "Aug 14", never with a year. For ranges that state the year once. */
export function monthDay(ledgerDate: string): string {
  const parts = parseLedgerDate(ledgerDate);
  if (!parts) return ledgerDate;
  return `${MONTHS[parts.month - 1]} ${parts.day}`;
}

/** "Aug 14", or "Aug 14, 2024" when the year is not the current one. */
export function shortDate(ledgerDate: string, now: Date = new Date()): string {
  const parts = parseLedgerDate(ledgerDate);
  if (!parts) return ledgerDate;
  if (parts.year === ledgerYear(now)) return monthDay(ledgerDate);
  return `${monthDay(ledgerDate)}, ${parts.year}`;
}

/** "Monday, Aug 14" — for grouping headers where the weekday helps orient. */
export function longDate(ledgerDate: string, now: Date = new Date()): string {
  const parts = parseLedgerDate(ledgerDate);
  if (!parts) return ledgerDate;
  const weekday = new Date(Date.UTC(parts.year, parts.month - 1, parts.day)).toLocaleDateString(
    'en-US',
    { weekday: 'long', timeZone: 'UTC' }
  );
  return `${weekday}, ${shortDate(ledgerDate, now)}`;
}
